/**
 *
 * TabStudents
 *
 */

import React, { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

// styles
import './style.scss';

// antd component
import { Form } from 'antd';

// components
import CustomTable from 'components/CustomTable';
import AddItemCard from 'components/AddItemCard';

import SelectFieldAxios from 'containers/SelectFieldAxios';

// utils
import {
  openNewRow,
  confirmSaveRowData,
  confirmRemoveRowData,
} from 'components/Tabs/helper.js';


function TabStudents({
  students,
  updateList,
  showForParent,
  showClass,
  className,
}) {
  const childClassNames = classNames(
    "tab-students-wrapper row-tabs",
    className,
  );
  const { t, i18n } = useTranslation();

  const [currentStudents, setCurrentStudents] = useState(students || []);
  const [tableRows, setTableRow] = useState([]);
  const [newElement, setNewElement] = useState({});
  const [showError, setShowError] = useState(false);

  // Form initialization for new row
  const [addNewFrom] = Form.useForm();

  // Set data for opened item
  useEffect(() => {
    setCurrentStudents(students);
    const rows =
      students?.length > 0 &&
      students
        ?.map(elem => {
          return {
            ...elem,
            firstname: elem?.description?.firstname,
            lastname: elem?.description?.lastname,
            showDelete: true || elem?.parents?.length > 1,
          };
        })
        .sort((a, b) =>
          a?.description?.lastname?.localeCompare(b?.description?.lastname),
        );
    setTableRow(rows);
  }, []);

  // Set data for opened item
  useEffect(() => {
    // set students list
    if (
      currentStudents?.length != students?.length ||
      JSON.stringify(currentStudents) !== JSON.stringify(students)
    ) {
      setCurrentStudents(students);
      const rows = students
        .map(elem => {
          return {
            ...elem,
            firstname: elem?.description?.firstname,
            lastname: elem?.description?.lastname,
            showDelete: true ||  elem?.parents?.length > 1,
          };
        })
        .sort((a, b) =>
          a?.description?.lastname?.localeCompare(b?.description?.lastname),
        );

      console.log({ rows, currentStudents, students });
      setTableRow(rows);
    }
  }, [students, currentStudents]);

  // create new Row with form
  const makeNewRow = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: `${t("common.new")} ${t("labels.student")}`,
      name: "new_student",
      endponit: "students/list",
      endponitSend: {},
      required: true,
      displayLabel: false,
      value: "",
      type: "axios-select",
      col: 24,
      onSelect: (LabeledValue, option) => {
        setNewElement(option?.item);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <Form form={addNewFrom} name="add-new-element" autoComplete="off">
        <SelectFieldAxios
          className="table-select"
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          itemCallback={newElementSelect.itemCallback}
          onSelect={newElementSelect.onSelect}
          item={newElementSelect}
        />
        {showError && (
          <span className="alert">{t("common.existInList")}</span>
        )}
      </Form>
    );
  }, [addNewFrom, showError, setNewElement, i18n?.language]);

  // Remove and update current Element
  const removeElement = elementObj => {
    if (elementObj && elementObj._id) {
      const newElements =
        currentStudents.length > 0 &&
        currentStudents.filter(el => el?._id !== elementObj?._id);
      setCurrentStudents(newElements);
      updateList(newElements, "deleteStudent");
    }
  };

  // save/add new Element form new Row
  const addElement = () => {
    addNewFrom.submit();
    const val = addNewFrom.getFieldsValue();
    if (val.new_student && newElement) {
      const hasElement =
        currentStudents &&
        currentStudents.some(element => element?._id === newElement?._id);

      if (!hasElement) {
        setShowError(false);
        const newEl = {
          _id: newElement?._id,
          description: newElement?.description,
        };
        const newEls = [newEl, ...currentStudents];
        setCurrentStudents(newEls);
        updateList(newEls, "addStudent");
      } else {
        setShowError(true);
      }
    }
  };

  const actionsColumn = {
    title: t("common.action"),
    dataIndex: "action",
    key: "action-column-student",
    className: "column-actions",
    render: (_, record) => {
      if (record.key === "initial") {
        return (
          <div className="row-actions">
            {confirmSaveRowData(tableRows, setTableRow, addElement)}
          </div>
        );
      } else {
        if ((!!showForParent && !!record?.showDelete) || !!!showForParent) {
          return confirmRemoveRowData(record, removeElement);
        }
      }
    },
  };

  const columns = [
    {
      title: t("common.lastNameShort"),
      dataIndex: "description.lastname",
      key: "column-lastname",
      className: "column-lastname",
      sorter: (a, b) =>
        a?.description?.lastname?.localeCompare(b?.description?.lastname),
      render: (text, record) => {
        if (record.key === "initial") {
          return {
            children: makeNewRow,
            props: {
              colSpan: !!showClass ? 3 : 2,
            },
          };
        } else {
          return {
            children: (
              <Link className="link-table" to={`/etudiants/${record._id}`}>
                {record?.description?.lastname}
              </Link>
            ),
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
    {
      title: t("common.firstName"),
      dataIndex: "firstname",
      key: "column-firstname",
      className: "column-firstname",
      sorter: (a, b) =>
        a?.description?.firstname?.localeCompare(b?.description?.firstname),
      render: (text, record, index) => {
        if (record.key === "initial") {
          return {
            children: "",
            props: {
              colSpan: 0,
            },
          };
        } else {
          return {
            children: (
              <Link className="link-table" to={`/etudiants/${record._id}`}>
                {record?.description?.firstname}
              </Link>
            ),
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
  ];

  const classColumn = {
    title: t("labels.class"),
    dataIndex: "className",
    key: "column-class",
    className: "column-class",
    sorter: (a, b) =>
      a?.class_info?.title?.localeCompare(b?.class_info?.title),
    render: (text, record, index) => {
      if (record.key === "initial") {
        return {
          children: "",
          props: {
            colSpan: 0,
          },
        };
      } else {
        return {
          children: (
            <Link
              className="link-table"
              to={`/classes/${record?.class_info?._id}`}
            >
              {record?.class_info?.title}
            </Link>
          ),
          props: {
            colSpan: 1,
          },
        };
      }
    },
  };

  if (!!showClass) {
    columns.push(classColumn);
  }

  // Set action column object
  columns.push(actionsColumn);

  // Render
  return (
    <div className={childClassNames}>
      <div className="column">
        <CustomTable dataSource={tableRows || []} columns={columns} />
      </div>
      <div className="column aside-column">
        <AddItemCard
          title={t("students.labelNameOf")}
          description={
            currentStudents &&
            `${currentStudents.length || 0} ${t(
              "students.addNewTabCardStudents",
            )}`
          }
          addAction={() => openNewRow(tableRows, setTableRow, addNewFrom)}
        />
      </div>
    </div>
  );
}

TabStudents.defaultProps = {
  students: [],
  showForParent: false,
  showClass: false,
};

TabStudents.propTypes = {
  students: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  updateList: PropTypes.func.isRequired,
};

export default memo(TabStudents);
